// lib/customer-schema.ts — Task B: single source of truth for the
// registration request shape. Used by both src/pages/Register.tsx
// (client-side pre-validation) and functions/api/customers/create.ts
// (server-side parse). Lives in the top-level lib/ directory because
// functions/ and src/ are separate compilation roots and cannot share
// imports otherwise.

import { z } from "zod";

// Stricter than lib/barcode-validator.ts's regex: enforces non-zero in
// the 4th and 8th positions, matching src/lib/barcode/barcode-input.client.ts.
// POKA-YOKE: any barcode that fails this on the client never reaches the
// server; any barcode that passes this on the client is guaranteed to
// pass the server's looser regex.
export const STRICT_BARCODE_REGEX = /^R\d{2}[1-9]\d{2}[1-9]\d{1}M\d{4}$/i;

// Phone: 01[0125] + 8 digits. Mirrors PHONE_REGEX in
// src/lib/barcode/barcode-input.client.ts (kept duplicated because
// workers and the browser can both import this shared module).
export const PHONE_REGEX = /^01[0125][0-9]{8}$/;

export const RegistrationRequestSchema = z.object({
  barcode: z
    .string()
    .min(13)
    .max(13)
    .regex(STRICT_BARCODE_REGEX, {
      message: "تنسيق الباركود غير صحيح (مثال: R2520160M0087)",
    }),
  name: z
    .string()
    .trim()
    .min(1, "الاسم مطلوب")
    .max(100, "الاسم طويل جداً"),
  phone: z
    .string()
    .regex(PHONE_REGEX, {
      message: "رقم الهاتف يجب أن يكون 11 رقماً ويبدأ بـ 01",
    }),
  date_of_birth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
      message: "تاريخ الميلاد يجب أن يكون بصيغة YYYY-MM-DD",
    })
    .optional(),
});

export type RegistrationRequest = z.infer<typeof RegistrationRequestSchema>;

// Server-side response shape, exported for the client to type its catch.
export const RegistrationErrorCode = {
  DUPLICATE_BARCODE: "DUPLICATE_BARCODE",
  DUPLICATE_PHONE: "DUPLICATE_PHONE",
  INVALID_PHONE: "INVALID_PHONE",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
} as const;

export type RegistrationErrorCode =
  (typeof RegistrationErrorCode)[keyof typeof RegistrationErrorCode];
